import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import logo from "../assets/img/logo.webp";
import logoWhite from "../assets/img/logo-white.webp";
import ScrollToTop from "../components/ScrollToTop";

import menuData from "../assets/data/menu";

/* ===
   SCHOOL HEADER THEMES
=== */

const SCHOOL_HEADER_THEME = {
  "school-of-management": "gold",
  "school-of-commerce": "gold",
  "school-of-engineering-and-technology": "gold",
  "school-of-information-and-technology": "gold",
  "school-of-hotel-management": "blue",
  "school-of-science": "gold",
  "school-of-arts-and-humanities": "blue",
  "school-of-law": "gold",
  "school-of-pharmacy": "blue",
  "school-of-design": "burgundy",
  "school-of-education": "burgundy",
  "school-of-journalism-and-mass-communication": "gold",
  "school-of-media-and-entertainment": "gold",
  "school-of-library-and-information-science": "blue",
  "school-of-yoga-and-naturopathy": "blue",
  "school-of-life-science": "gold",
  "school-of-vocational-management": "blue",
  "school-of-health-and-allied-science": "burgundy",
  common: "gold",
};

/* ===
   HEADER THEME CLASSES
=== */

const HEADER_THEME_CLASSES = {
  transparent: {
    header: "bg-transparent text-white",
    scrolled: "bg-[#071A2F]/95 text-white shadow-[0_14px_42px_rgba(0,0,0,.18)] backdrop-blur-xl fixed",
    navSurface: "bg-white/[0.08] shadow-[0_14px_36px_rgba(0,0,0,.08)]",
    navHover: "hover:bg-white/[0.12]",
    button: "",
    active: "bg-white/[0.12]",
    utilityText: "text-white/75 hover:text-white",
    logoIsWhite: true,
  },

  white: {
    header: "bg-white text-[#071A2F]",
    scrolled: "bg-white text-[#071A2F] shadow-[0_14px_42px_rgba(7,26,47,.12)] backdrop-blur-xl fixed",
    navSurface: "bg-[#071A2F]/[0.055] shadow-[0_12px_34px_rgba(7,26,47,.055)]",
    navHover: "hover:bg-[#8B1E3F]/20",
    button: "",
    active: "bg-[#8B1E3F]/10",
    utilityText: "text-[#071A2F]/65 hover:text-[#8B1E3F]",
    logoIsWhite: false,
  },

  gold: {
    header: "bg-[#f7f2e8] text-[#071A2F]",
    scrolled: "bg-[#f7f2e8] text-[#071A2F] shadow-[0_14px_42px_rgba(7,26,47,.12)] backdrop-blur-xl fixed",
    navSurface: "bg-[#071A2F]/[0.055] shadow-[0_12px_34px_rgba(7,26,47,.055)]",
    navHover: "hover:bg-[#8B1E3F]/20",
    button: "",
    active: "bg-[#8B1E3F]/10",
    utilityText: "text-[#071A2F]/65 hover:text-[#8B1E3F]",
    logoIsWhite: false,
  },

  blue: {
    header: "bg-[#071A2F] text-white",
    scrolled: "bg-[#071A2F]/95 text-white shadow-[0_14px_42px_rgba(0,0,0,.18)] backdrop-blur-xl fixed",
    navSurface: "bg-white/[0.08] shadow-[0_14px_36px_rgba(0,0,0,.08)]",
    navHover: "hover:bg-white/[0.12]",
    button: "",
    active: "bg-white/[0.12]",
    utilityText: "text-white/75 hover:text-white",
    logoIsWhite: true,
  },

  burgundy: {
    header: "bg-[#8B1E3F] text-white",
    scrolled: "bg-[#8B1E3F]/95 text-white shadow-[0_14px_42px_rgba(0,0,0,.18)] backdrop-blur-xl fixed",
    navSurface: "bg-white/[0.09] shadow-[0_14px_36px_rgba(0,0,0,.08)]",
    navHover: "hover:bg-white/[0.13]",
    button: "",
    active: "bg-white/[0.13]",
    utilityText: "text-white/75 hover:text-white",
    logoIsWhite: true,
  },
};

/* ===
   INDIVIDUAL PROGRAMME SCHOOL

   This component handles dynamic positioning
   of the dependent course dropdown.
=== */

function ProgrammeSchoolItem({
  school,
  hoveredSchool,
  setHoveredSchool,
}) {
  const schoolRef = useRef(null);
  const dropdownRef = useRef(null);

  const [dropdownStyle, setDropdownStyle] = useState({
    visibility: "hidden",
    top: "0px",
    left: "0px",
    maxHeight: "none",
  });

  const isActive = hoveredSchool === school && Array.isArray(school.courses) && school.courses.length > 0;

  /* =====
     CALCULATE DROPDOWN POSITION
  ===== */

  const calculateDropdownPosition = useCallback(() => {
    if (!schoolRef.current || !dropdownRef.current) {
      return;
    }

    const schoolElement = schoolRef.current;
    const dropdownElement = dropdownRef.current;

    /*
     * Find the actual white mega-menu parent.
     */
    const megaMenu = schoolElement.closest('[data-mega-menu="true"]');

    if (!megaMenu) {
      return;
    }

    const triggerRect = schoolElement.getBoundingClientRect();
    const parentRect = megaMenu.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportPadding = 10;
    const horizontalGap = 8;

    /*
     * 
     * PARENT HEIGHT
     *
     * This is the maximum height allowed
     * for the dependent dropdown.
     * 
     */

    const parentHeight = parentRect.height;

    /*
     * Actual dropdown dimensions.
     */
    const dropdownWidth = dropdownElement.offsetWidth;
    const naturalDropdownHeight = dropdownElement.scrollHeight;

    /*
     * Dropdown can NEVER become taller
     * than the main white mega-menu.
     */
    const dropdownHeight = Math.min(naturalDropdownHeight, parentHeight);

    /* =====
       HORIZONTAL POSITIONING
    ===== */

    const spaceOnRight = viewportWidth - triggerRect.right - viewportPadding;
    const spaceOnLeft = triggerRect.left - viewportPadding;
    let left;

    /*
     * First preference:
     * open to the RIGHT.
     */
    if (
      spaceOnRight >=
      dropdownWidth + horizontalGap
    ) {
      left =
        triggerRect.right +
        horizontalGap;
    }

    /*
     * If right side doesn't have enough space,
     * open to the LEFT.
     */
    else if (
      spaceOnLeft >=
      dropdownWidth + horizontalGap
    ) {
      left =
        triggerRect.left -
        dropdownWidth -
        horizontalGap;
    }

    /*
     * Fallback:
     * keep dropdown completely inside viewport.
     */
    else {
      const preferredLeft =
        triggerRect.right +
        horizontalGap;

      left = Math.max(
        viewportPadding,
        Math.min(
          preferredLeft,
          viewportWidth -
          dropdownWidth -
          viewportPadding
        )
      );
    }

    /* =====
       VERTICAL POSITIONING
    ===== */

    /*
     * By default align the top of dropdown
     * with the hovered school.
     */
    let top =
      triggerRect.top;

    /*
     * If dropdown would cross the bottom
     * of the white mega-menu,
     * move it upward.
     */
    if (
      top + dropdownHeight >
      parentRect.bottom
    ) {
      top =
        parentRect.bottom -
        dropdownHeight;
    }

    /*
     * Never allow it to cross above
     * the white mega-menu.
     */
    if (top < parentRect.top) {
      top =
        parentRect.top;
    }

    /*
     * Final horizontal viewport safety.
     */
    left = Math.max(
      viewportPadding,
      Math.min(
        left,
        viewportWidth -
        dropdownWidth -
        viewportPadding
      )
    );

    setDropdownStyle({
      visibility: "visible",

      top: `${Math.round(top)}px`,

      left: `${Math.round(left)}px`,

      /*
       * MOST IMPORTANT:
       *
       * Same maximum height as
       * parent white mega-menu.
       */
      maxHeight: `${Math.round(
        parentHeight
      )}px`,
    });
  }, []);

  /* =====
     POSITION AFTER DROPDOWN APPEARS
  ===== */

  useLayoutEffect(() => {
    if (!isActive) {
      return;
    }

    calculateDropdownPosition();

    const handleResize = () => {
      window.requestAnimationFrame(
        calculateDropdownPosition
      );
    };

    const handleScroll = () => {
      window.requestAnimationFrame(
        calculateDropdownPosition
      );
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    /*
     * true allows us to detect scroll events
     * from any scrolling container too.
     */
    window.addEventListener(
      "scroll",
      handleScroll,
      true
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );

      window.removeEventListener(
        "scroll",
        handleScroll,
        true
      );
    };
  }, [
    isActive,
    calculateDropdownPosition,
  ]);

  /* =====
     SCHOOL ITEM
  ===== */

  return (
    <div
      ref={schoolRef}
      className="school-item"
      onMouseEnter={() =>
        setHoveredSchool(school)
      }
      onMouseLeave={() =>
        setHoveredSchool(null)
      }
    >
      <a
        href={school.url || "#"}
        className="
          flex
          items-center
          justify-between
          border-b
          border-[#EDF0F2]
          py-2.5
          text-sm
          transition
          hover:pl-1
          hover:text-[#9A762F]
        "
      >
        <span>
          {school.title}
        </span>

        {school.courses?.length >
          0 && (
            <span
              className="
              ml-3
              shrink-0
              text-[#D5AD57]
            "
            >
              ›
            </span>
          )}
      </a>

      {/* =
          DEPENDENT COURSE DROPDOWN
      = */}

      {isActive && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="
            course-dropdown

            fixed

            z-[99999]

            w-[320px]
            max-w-[calc(100vw-20px)]

            overflow-x-hidden
            overflow-y-auto
            custom-scroll

            overscroll-contain

            rounded-xl

            border
            border-[#E2E5E9]

            bg-white

            text-[#071A2F]

            shadow-[0_18px_45px_rgba(7,26,47,.18)]
          "
        >
          {school.courses.map(
            (course) => (
              <a
                key={
                  course.url ||
                  course.title
                }
                href={
                  course.url ||
                  "#"
                }
                className="
                  group/course

                  flex
                  min-h-[44px]
                  items-center
                  justify-between

                  border-b
                  border-[#EDF0F2]

                  px-4
                  py-2.5

                  text-[13px]
                  font-medium
                  leading-5

                  text-[#253648]

                  transition

                  last:border-b-0

                  hover:bg-[#F7F2E8]
                  hover:text-[#8B1E3F]
                "
              >
                <span>
                  {course.title}
                </span>

                <span
                  className="
                    ml-3
                    shrink-0
                    text-[#D5AD57]
                  "
                >
                  →
                </span>
              </a>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* ===
   PROGRAMME GROUP
=== */

function ProgrammeGroup({
  group,
  className = "",
  hoveredSchool,
  setHoveredSchool,
}) {
  if (!group) {
    return null;
  }

  return (
    <div
      className={`
        mega-group
        ${className}
      `.trim()}
    >
      <h3
        className="
          mb-4
          mt-1
          text-sm
          font-bold
          uppercase
          tracking-[0.16em]
          text-[#8D6B2E]
        "
      >
        {group.title}
      </h3>

      {group.links?.map(
        (school) => (
          <ProgrammeSchoolItem
            key={
              school.url ||
              school.title
            }
            school={school}
            hoveredSchool={
              hoveredSchool
            }
            setHoveredSchool={
              setHoveredSchool
            }
          />
        )
      )}
    </div>
  );
}

/* ===
   UNIVERSITY HEADER
=== */

function UniversityHeader({
  theme = "transparent",
}) {
  const [menu, setMenu] =
    useState(null);

  const [
    mobileOpen,
    setMobileOpen,
  ] = useState(false);

  const [
    mobileSection,
    setMobileSection,
  ] = useState(null);

  const [
    scrolled,
    setScrolled,
  ] = useState(false);

  const [
    hoveredSchool,
    setHoveredSchool,
  ] = useState(null);

  /* =====
     MENU DATA
  ===== */

  const menuEntries = useMemo(
    () =>
      Object.entries(
        menuData || {}
      ),
    []
  );

  const activeMenu =
    menu
      ? menuData?.[menu]
      : null;

  const themeClasses =
    HEADER_THEME_CLASSES[
    theme
    ] ||
    HEADER_THEME_CLASSES.white;

  const useWhiteLogo =
    themeClasses.logoIsWhite;

  /* =====
     UTILITY LINKS
  ===== */

  const utilityLinks = [
    {
      title: "Student Login",
      url: "/student-login",
    },

    {
      title: "NAAC",
      url: "/naac",
    },

    {
      title: "IQAC",
      url: "/iqac",
    },

    {
      title: "e-Library",
      url: "/e-library",
    },

    {
      title: "Ph.D Research",
      url: "/phd-research",
    },

    {
      title: "Pay Online",
      url: "/pay-online",
    },
  ];

  /* =====
     SCROLL HEADER
  ===== */

  useEffect(() => {
    const onScroll = () => {
      setScrolled(
        window.scrollY > 30
      );
    };

    onScroll();

    window.addEventListener(
      "scroll",
      onScroll,
      {
        passive: true,
      }
    );

    return () => {
      window.removeEventListener(
        "scroll",
        onScroll
      );
    };
  }, []);

  /* =====
     MOBILE BODY SCROLL LOCK
  ===== */

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      mobileOpen
        ? "hidden"
        : previousOverflow;

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [mobileOpen]);

  /* =====
     RESET DEPENDENT DROPDOWN
  ===== */

  useEffect(() => {
    setHoveredSchool(null);
  }, [menu]);

  const closeMobileMenu =
    () => {
      setMobileOpen(false);
      setMobileSection(null);
    };

  return (
    <>
      <ScrollToTop />

      {/* =
          HEADER
      = */}

      <header
        className={`
          inset-x-0
          top-0
          z-[100]
          transition-all
          duration-300

          ${scrolled
            ? themeClasses.scrolled
            : themeClasses.header
          }
        `}
        onMouseLeave={() => {
          setMenu(null);
          setHoveredSchool(null);
        }}
      >
        {/* =
            HEADER INNER
        = */}

        <div
          className="
            mx-auto
            flex
            h-[132px]
            max-w-[1480px]
            gap-[clamp(34px,2vw,68px)]
          "
        >
          {/* LOGO */}

          <a
            className="
              flex
              shrink-0
              items-center
              justify-center
              bg-transparent
              py-3
            "
            href="/"
            aria-label="ISBM University home"
          >
            <img
              src={
                useWhiteLogo
                  ? logoWhite ||
                  logo
                  : logo
              }
              className="
                h-full
                max-h-[108px]
                w-auto
                object-contain
              "
              alt="ISBM University"
            />
          </a>

          {/* =
              DESKTOP HEADER
          = */}

          <div
            className="
              min-w-0
              flex-1
              py-2.5
              max-lg:hidden
            "
          >
            {/* TOP UTILITY NAVIGATION */}

            <div
              className="
                flex
                h-[38px]
                items-center
                justify-between
                px-3
              "
            >
              <nav
                className="
                  flex
                  h-full
                  items-center
                  gap-[24px]
                "
                aria-label="Utility navigation"
              >
                {utilityLinks.map(
                  (link) => (
                    <a
                      className={`
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-[0.08em]
                        transition

                        ${themeClasses.utilityText}
                      `}
                      key={
                        link.url ||
                        link.title
                      }
                      href={
                        link.url ||
                        "#"
                      }
                    >
                      {link.title}
                    </a>
                  )
                )}
              </nav>

              {/* CONTACT + APPLY */}

              <div
                className="
                  flex
                  h-full
                  items-center
                "
              >
                <a
                  href="tel:+919373199999"
                  className={`
                    px-5
                    text-xs
                    font-semibold

                    ${themeClasses.utilityText}
                  `}
                >
                  <span
                    className="
                      mr-1.5
                      opacity-60
                    "
                  >
                    Call us
                  </span>

                  +91 93731 99999
                </a>

                <a
                  href="/apply"
                  className="
                    flex
                    h-10
                    items-center
                    gap-5
                    rounded-full
                    bg-[#D5AD57]
                    px-5
                    text-xs
                    font-bold
                    uppercase
                    tracking-[0.08em]
                    text-[#071A2F]
                    transition
                    hover:-translate-y-0.5
                    hover:bg-white
                  "
                >
                  Apply Online

                  <span
                    className="
                      text-base
                    "
                  >
                    ↗
                  </span>
                </a>
              </div>
            </div>

            {/* PRIMARY NAVIGATION */}

            <nav
              className={`
                mt-6
                flex
                h-[48px]
                items-center
                rounded-full
                px-2

                ${themeClasses.navSurface}
              `}
              aria-label="Primary navigation"
            >
              {menuEntries.map(
                ([item]) => (
                  <button
                    key={item}
                    type="button"
                    className={`
                      group
                      relative
                      flex
                      h-[34px]
                      items-center
                      rounded-full
                      border-0
                      bg-transparent
                      px-[9px]
                      text-[13px]
                      font-semibold
                      transition

                      ${themeClasses.button}

                      ${menu ===
                        item
                        ? themeClasses.active
                        : ""
                      }
                    `}
                    onMouseEnter={() =>
                      setMenu(
                        item
                      )
                    }
                    onFocus={() =>
                      setMenu(
                        item
                      )
                    }
                    aria-expanded={
                      menu ===
                      item
                    }
                  >
                    {item}

                    <span
                      className="
                        ml-[2px]
                        text-sm
                        text-[#D5AD57]
                      "
                    >
                      <i
                        className="
                          ti
                          ti-chevrons-down
                        "
                      />
                    </span>

                    <span
                      className={`
                        absolute
                        inset-x-4
                        bottom-1
                        h-0.5
                        origin-center
                        bg-[#D5AD57]
                        transition-transform

                        ${menu ===
                          item
                          ? "scale-x-100"
                          : "scale-x-0 group-hover:scale-x-100"
                        }
                      `}
                    />
                  </button>
                )
              )}

              {/* SEARCH */}

              <button
                type="button"
                className={`
                  ml-auto
                  grid
                  h-11
                  w-11
                  place-items-center
                  rounded-full
                  border-0
                  bg-transparent
                  transition

                  ${themeClasses.navHover}
                `}
                aria-label="Search"
              >
                <svg
                  className="
                    h-5
                    w-5
                    fill-none
                    stroke-current
                    stroke-[1.5]
                  "
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="6.5"
                  />

                  <path
                    d="m16 16 4 4"
                  />
                </svg>
              </button>
            </nav>
          </div>

          {/* =
              MOBILE HEADER BUTTONS
          = */}

          <div
            className="
              ml-auto
              hidden
              items-center
              gap-3
              max-lg:flex
            "
          >
            <a
              href="/apply"
              className="
                rounded-full
                bg-[#D5AD57]
                px-4
                py-2
                text-xs
                font-bold
                text-[#071A2F]
              "
            >
              Apply
            </a>

            <button
              type="button"
              className={`
                menu-toggle
                grid
                h-11
                w-11
                place-content-center
                gap-1.5
                rounded-full
                bg-current/10

                ${mobileOpen
                  ? "open"
                  : ""
                }
              `}
              onClick={() =>
                setMobileOpen(
                  (open) =>
                    !open
                )
              }
              aria-expanded={
                mobileOpen
              }
              aria-controls="mobile-navigation-panel"
              aria-label="Toggle menu"
            >
              <span
                className="
                  block
                  h-0.5
                  w-5
                  bg-current
                "
              />

              <span
                className="
                  block
                  h-0.5
                  w-5
                  bg-current
                "
              />

              <span
                className="
                  block
                  h-0.5
                  w-5
                  bg-current
                "
              />
            </button>
          </div>
        </div>

        {/* =
            DESKTOP MEGA MENU

            data-mega-menu is used by the dependent
            dropdown to read this container's real height.
        = */}

        <div
          data-mega-menu="true"
          className={`
            absolute
            inset-x-0
            top-[132px]
            z-[1000]

            bg-white
            text-[#071A2F]

            shadow-[0_20px_45px_rgba(0,0,0,.2)]

            transition-[max-height,opacity]
            duration-300

            ${activeMenu
              ? "max-h-[520px] overflow-visible opacity-100"
              : "max-h-0 overflow-hidden opacity-0"
            }
          `}
          role="region"
          aria-label="Navigation submenu"
        >
          {activeMenu && (
            <div
              className="
                mx-auto
                grid
                max-w-[1400px]
                grid-cols-[1.25fr_.8fr_.8fr_1fr]
                gap-11
                px-12
                py-10
              "
              key={menu}
            >
              {/*   MEGA MENU INTRO
              = */}

              <div
                className="
                  border-r
                  border-[#E4E6E8]
                  pr-7
                "
              >
                <span
                  className="
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.2em]
                    text-[#9A762F]
                  "
                >
                  {
                    activeMenu.eyebrow
                  }
                </span>

                <h2
                  className="
                    my-3
                    font-['Libre_Caslon_Display',Georgia,serif]
                    text-3xl
                    font-normal
                    leading-[1.16]
                  "
                >
                  {
                    activeMenu.title
                  }
                </h2>

                <p
                  className="
                    text-sm
                    leading-7
                    text-[#65707D]
                  "
                >
                  {
                    activeMenu.description
                  }
                </p>

                <a
                  className="
                    mt-3
                    inline-flex
                    gap-6
                    text-sm
                    font-bold
                    uppercase
                    tracking-[0.1em]
                  "
                  href={
                    activeMenu.url ||
                    "#"
                  }
                >
                  Overview

                  <span>
                    →
                  </span>
                </a>
              </div>

              {/*   PROGRAMME COLUMNS
              = */}

              {menu ===
                "Programmes" ? (
                <>
                  <ProgrammeGroup
                    group={
                      activeMenu
                        .groups?.[0]
                    }
                    className="mega-relative"
                    hoveredSchool={
                      hoveredSchool
                    }
                    setHoveredSchool={
                      setHoveredSchool
                    }
                  />

                  <ProgrammeGroup
                    group={
                      activeMenu
                        .groups?.[1]
                    }
                    className="mega-relative-2"
                    hoveredSchool={
                      hoveredSchool
                    }
                    setHoveredSchool={
                      setHoveredSchool
                    }
                  />
                </>
              ) : (
                activeMenu.groups?.map(
                  (group) => (
                    <div
                      className="mega-group"
                      key={
                        group.title
                      }
                    >
                      <h3
                        className="
                          mb-4
                          mt-1
                          text-sm
                          font-bold
                          uppercase
                          tracking-[0.16em]
                          text-[#8D6B2E]
                        "
                      >
                        {
                          group.title
                        }
                      </h3>

                      {group.links?.map(
                        (
                          link
                        ) => (
                          <a
                            className="
                              flex
                              justify-between
                              border-b
                              border-[#EDF0F2]
                              py-2.5
                              text-sm
                              transition
                              hover:px-2
                              hover:text-[#9A762F]
                            "
                            key={
                              link.url ||
                              link.title
                            }
                            href={
                              link.url ||
                              "#"
                            }
                          >
                            {
                              link.title
                            }
                          </a>
                        )
                      )}
                    </div>
                  )
                )
              )}

              {/*   FEATURED IMAGE
              = */}

              <a
                href={
                  activeMenu.url ||
                  "#"
                }
                className="
                  group
                  relative
                  h-[218px]
                  overflow-hidden
                  text-white
                "
              >
                {activeMenu.image && (
                  <img
                    className="
                      h-full
                      w-full
                      object-cover
                      transition
                      duration-500
                      group-hover:scale-105
                    "
                    src={
                      activeMenu.image
                    }
                    alt=""
                  />
                )}

                <span
                  className="
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-[#031222]/90
                    to-transparent
                  "
                />

                <span
                  className="
                    absolute
                    inset-x-5
                    bottom-5
                    z-10
                    flex
                    flex-col
                  "
                >
                  <small
                    className="
                      text-xs
                      font-bold
                      uppercase
                      tracking-[0.15em]
                      text-[#D5AD57]
                    "
                  >
                    Featured
                  </small>

                  <strong
                    className="
                      my-2
                      font-['Libre_Caslon_Display',Georgia,serif]
                      text-xl
                      font-normal
                    "
                  >
                    Experience our
                    university
                  </strong>

                  <i
                    className="
                      text-xs
                      not-italic
                    "
                  >
                    Explore now →
                  </i>
                </span>
              </a>
            </div>
          )}
        </div>
      </header>

      {/* =
          MOBILE BACKDROP
      = */}

      <button
        type="button"
        aria-label="Close mobile navigation"
        tabIndex={
          mobileOpen
            ? 0
            : -1
        }
        onClick={
          closeMobileMenu
        }
        className={`
          fixed
          inset-0
          z-[90]

          hidden

          bg-[#071A2F]/55
          backdrop-blur-sm

          transition-opacity

          max-lg:block

          ${mobileOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
          }
        `}
      />

      {/* =
          MOBILE NAVIGATION PANEL
      = */}

      <div
        id="mobile-navigation-panel"
        className={`
          fixed
          inset-x-0
          bottom-0
          top-[132px]

          z-[95]

          hidden

          overflow-y-auto

          bg-[#F7F2E8]
          text-[#071A2F]

          shadow-[0_24px_70px_rgba(7,26,47,.25)]

          transition
          duration-300

          max-lg:block

          ${mobileOpen
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-5 opacity-0"
          }
        `}
        aria-hidden={
          !mobileOpen
        }
      >
        {/* MOBILE UTILITY LINKS */}

        <div
          className="
            sticky
            top-0
            z-10

            flex
            flex-wrap
            items-center

            gap-x-5
            gap-y-2

            bg-[#071A2F]

            px-5
            py-4

            text-white

            sm:px-8
          "
        >
          {utilityLinks.map(
            (link) => (
              <a
                key={
                  link.url ||
                  link.title
                }
                href={
                  link.url ||
                  "#"
                }
                onClick={
                  closeMobileMenu
                }
                className="
                  text-xs
                  font-bold
                  uppercase
                  tracking-[0.1em]
                  text-white/65
                  transition
                  hover:text-[#D5AD57]
                "
              >
                {
                  link.title
                }
              </a>
            )
          )}

          <a
            href="tel:+919373199999"
            className="
              ml-auto
              text-xs
              font-bold
              uppercase
              tracking-[0.1em]
              text-[#D5AD57]
            "
          >
            Call Us
          </a>
        </div>

        {/* =
            MOBILE PRIMARY NAVIGATION
        = */}

        <nav
          className="
            mx-auto
            w-full
            max-w-3xl
            px-5
            py-5
            sm:px-8
          "
          aria-label="Mobile navigation"
        >
          {Object.entries(
            menuData
          ).map(
            (
              [
                item,
                data,
              ],
              itemIndex
            ) => {
              const isOpen =
                mobileSection ===
                item;

              return (
                <div
                  className="
                    border-b
                    border-[#071A2F]/10
                  "
                  key={item}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setMobileSection(
                        isOpen
                          ? null
                          : item
                      )
                    }
                    aria-expanded={
                      isOpen
                    }
                    className="
                      grid
                      min-h-[74px]
                      w-full
                      grid-cols-[38px_1fr_42px]
                      items-center
                      gap-3
                      text-left
                    "
                  >
                    <span
                      className="
                        text-xs
                        font-bold
                        text-[#8B1E3F]
                      "
                    >
                      {String(
                        itemIndex +
                        1
                      ).padStart(
                        2,
                        "0"
                      )}
                    </span>

                    <strong
                      className="
                        font-['Libre_Caslon_Display',Georgia,serif]
                        text-2xl
                        font-normal
                        text-[#071A2F]
                      "
                    >
                      {item}
                    </strong>

                    <span
                      className={`
                        grid
                        h-9
                        w-9
                        place-items-center
                        rounded-full
                        text-xl
                        transition

                        ${isOpen
                          ? "rotate-180 bg-[#8B1E3F] text-white"
                          : "bg-[#071A2F]/[0.06] text-[#8B1E3F]"
                        }
                      `}
                    >
                      {isOpen
                        ? "−"
                        : "+"}
                    </span>
                  </button>

                  {/* MOBILE SUBMENU */}

                  <div
                    className={`
                      grid

                      transition-[grid-template-rows,opacity]
                      duration-300

                      ${isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                      }
                    `}
                  >
                    <div
                      className="
                        overflow-hidden
                      "
                    >
                      <div
                        className="
                          grid
                          gap-7
                          pb-7
                          pl-[51px]
                          sm:grid-cols-2
                        "
                      >
                        {data.groups?.map(
                          (
                            group
                          ) => (
                            <div
                              key={
                                group.title
                              }
                            >
                              <h6
                                className="
                                  mb-3
                                  text-xs
                                  font-extrabold
                                  uppercase
                                  tracking-[0.14em]
                                  text-[#A77B2D]
                                "
                              >
                                {
                                  group.title
                                }
                              </h6>

                              <div
                                className="
                                  space-y-1
                                "
                              >
                                {group.links?.map(
                                  (
                                    link
                                  ) => (
                                    <React.Fragment
                                      key={
                                        link.url ||
                                        link.title
                                      }
                                    >
                                      <a
                                        href={
                                          link.url ||
                                          "#"
                                        }
                                        onClick={
                                          closeMobileMenu
                                        }
                                        className="
                                          flex
                                          min-h-10
                                          items-center
                                          justify-between
                                          rounded-xl
                                          px-3
                                          text-sm
                                          font-semibold
                                          text-[#071A2F]
                                          transition
                                          hover:bg-white
                                          hover:text-[#8B1E3F]
                                        "
                                      >
                                        {
                                          link.title
                                        }

                                        <span
                                          className="
                                            text-[#D5AD57]
                                          "
                                        >
                                          →
                                        </span>
                                      </a>

                                      {link.courses?.map(
                                        (
                                          course
                                        ) => (
                                          <a
                                            href={
                                              course.url ||
                                              "#"
                                            }
                                            key={
                                              course.url ||
                                              course.title
                                            }
                                            onClick={
                                              closeMobileMenu
                                            }
                                            className="
                                              ml-3
                                              flex
                                              min-h-9
                                              items-center
                                              border-l
                                              border-[#D5AD57]
                                              pl-4
                                              text-xs
                                              font-medium
                                              text-[#65717D]
                                              transition
                                              hover:text-[#8B1E3F]
                                            "
                                          >
                                            {
                                              course.title
                                            }
                                          </a>
                                        )
                                      )}
                                    </React.Fragment>
                                  )
                                )}
                              </div>
                            </div>
                          )
                        )}

                        <a
                          href={
                            data.url ||
                            "#"
                          }
                          onClick={
                            closeMobileMenu
                          }
                          className="
                            inline-flex
                            min-h-11
                            items-center
                            gap-4
                            self-end
                            rounded-full
                            bg-[#071A2F]
                            px-5
                            text-xs
                            font-bold
                            uppercase
                            tracking-[0.08em]
                            text-white
                            transition
                            hover:bg-[#8B1E3F]
                            sm:col-span-2
                            sm:justify-self-start
                          "
                        >
                          View {item}

                          <span
                            className="
                              text-[#D5AD57]
                            "
                          >
                            →
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </nav>

        {/* =
            MOBILE CTA
        = */}

        <div
          className="
            sticky
            bottom-0
            mt-5
            bg-[#F7F2E8]/95
            p-5
            shadow-[0_-15px_35px_rgba(7,26,47,.08)]
            backdrop-blur
            sm:px-8
          "
        >
          <a
            href="/apply"
            onClick={
              closeMobileMenu
            }
            className="
              mx-auto
              flex
              min-h-14
              max-w-3xl
              items-center
              justify-between
              rounded-full
              bg-[#8B1E3F]
              px-6
              text-sm
              font-bold
              text-white
              transition
              hover:bg-[#071A2F]
            "
          >
            Begin your
            application

            <span
              className="
                grid
                h-9
                w-9
                place-items-center
                rounded-full
                bg-[#D5AD57]
                text-[#071A2F]
              "
            >
              →
            </span>
          </a>
        </div>
      </div>
    </>
  );
}

/* ===
   HEADER VARIANTS
=== */

export function HeaderWhite(
  props
) {
  return (
    <UniversityHeader
      {...props}
      theme="white"
    />
  );
}

export function HeaderTransparent(
  props
) {
  return (
    <UniversityHeader
      {...props}
      theme="transparent"
    />
  );
}

export function HeaderBlue(
  props
) {
  return (
    <UniversityHeader
      {...props}
      theme="blue"
    />
  );
}

export function HeaderBurgundy(
  props
) {
  return (
    <UniversityHeader
      {...props}
      theme="burgundy"
    />
  );
}

/* ===
   DEFAULT HEADER
=== */

export default function Header({
  school,
  schoolDetails,
  theme: themeOverride,
}) {
  const schoolObject =
    typeof school ===
      "object" &&
      school !== null
      ? school
      : schoolDetails;

  const schoolId =
    typeof school ===
      "string"
      ? school
      : schoolObject?.id ||
      schoolObject?.slug;

  const theme =
    themeOverride ||
    SCHOOL_HEADER_THEME[
    schoolId
    ];

  console.log(
    "theme:",
    theme
  );

  if (!theme) {
    return null;
  }

  return (
    <UniversityHeader
      theme={theme}
    />
  );
}

export {
  SCHOOL_HEADER_THEME,
};